import React, { useState, useEffect } from "react";
import { SaveButton } from "../../Componentes/inputs/SaveButton";
import { BotonSecondary } from "../../Componentes/BotonSecondary";
import { useBody } from "../../Body/state/BodyContext";
import {
  Box,
  DialogActions,
  DialogContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import { useTranslation } from "../translationContext";
import keycloak from "../../Auth/Keycloak";

const IniciarSolicitudModal = ({ onClose }) => {
  const { createTranslation, getDepartment, getReasons, getTypes } =
    useTranslation();
  const { setMessage, setSuccess } = useBody();
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    titulo: "",
    adjunto: null,
    tarea: "",
    fechaLimite: "",
    departamento: "",
    motivo: "",
    proyectoInvestigacion: "",
  });
  const [tareas, setTareas] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [motivos, setMotivos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    try {
      const tareasData = await getTypes();
      const departamentosData = await getDepartment();
      const motivosData = await getReasons();

      if (
        !Array.isArray(tareasData) ||
        !Array.isArray(departamentosData) ||
        !Array.isArray(motivosData)
      ) {
        setError("Hubo un error al intentar obtener los datos del formulario.");
      } else {
        setTareas(tareasData);
        setDepartamentos(departamentosData);
        setMotivos(motivosData);
      }
    } catch (error) {
      setError("Hubo un error al intentar obtener los datos del formulario.");
      setMessage("Error al intentar obtener los datos del formulario.");
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleChange = (e) => {
    const { name, value, files } = e.target;

    if (files && files[0]) {
      const file = files[0];
      const maxSizeInMB = 10;
      const maxSizeInBytes = maxSizeInMB * 1024 * 1024;

      if (file.size > maxSizeInBytes) {
        setMessage(`El archivo no debe exceder los ${maxSizeInMB}MB.`);
        return;
      }

      if (
        file.type !==
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
      ) {
        setMessage("Solo se permiten archivos con extensión .docx.");
        return;
      }
    }

    setFormData({
      ...formData,
      [name]: files ? files[0] : value,
    });
  };

  const isTitleValid =
    formData.titulo.length >= 5 && formData.titulo.length <= 100;
  const isFechaLimiteValid =
    formData.fechaLimite && new Date(formData.fechaLimite) > new Date();
  const isAdjuntoValid = formData.adjunto !== null;
  const isTareaValid = formData.tarea !== "";
  const isDepartamentoValid = formData.departamento !== "";
  const isMotivoValid = formData.motivo !== "";
  const isProyectoInvestigacionValid = formData.proyectoInvestigacion !== "";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(true);

    if (!isTitleValid) {
      setMessage("El título debe tener entre 5 y 100 caracteres.");
      return;
    }
    if (!isFechaLimiteValid) {
      setMessage("La fecha límite debe ser mayor a la de hoy.");
      return;
    }
    if (!isAdjuntoValid) {
      setMessage("Debe adjuntar un archivo menor a 10MB.");
      return;
    }
    if (
      !isTareaValid ||
      !isDepartamentoValid ||
      !isMotivoValid ||
      !isProyectoInvestigacionValid
    ) {
      setMessage("Debe seleccionar una opción en todos los campos.");
      return;
    }

    const jwt = keycloak.getToken();
    const decodedToken = JSON.parse(atob(jwt.split(".")[1]));
    const petitioner_id = decodedToken.sub;
    const petitioner_name =
      decodedToken.preferred_username || decodedToken.name;

    const payload = {
      petitioner_id,
      petitioner_name,
      title: formData.titulo,
      status_id: 1,
      type_id: formData.tarea,
      reason_id: formData.motivo,
      pid_utn: formData.proyectoInvestigacion === "si" ? 1 : 0,
      deadline: formData.fechaLimite,
      department_id: formData.departamento,
      file: formData.adjunto,
    };

    try {
      setLoading(true);
      await createTranslation(payload);
      onClose();
      setSuccess("Solicitud creada con éxito.");
    } catch (error) {
      setMessage("Error al crear la solicitud.");
      console.error("Error al crear la traducción:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            marginTop: "220px",
          }}
        >
          <CircularProgress />
        </div>
      ) : error ? (
        <div
          style={{
            display: "flex",
            color: "red",
            justifyContent: "center",
            alignItems: "center",
            marginTop: "220px",
          }}
        >
          {error}
        </div>
      ) : (
        <Box marginBottom="25px">
          <DialogContent>
            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{ display: "flex", flexDirection: "column", gap: 2 }}
            >
              <TextField
                name="titulo"
                label="Título"
                variant="outlined"
                value={formData.titulo}
                onChange={handleChange}
                fullWidth
                error={submitted && !isTitleValid}
                helperText={
                  submitted && !isTitleValid
                    ? "El título debe tener entre 5 y 100 caracteres."
                    : ""
                }
              />
              <p style={{ margin: 0, padding: 0, fontSize: 13 }}>
                * El titulo debe contener más de 5 caracteres.
              </p>

              <FormControl fullWidth error={submitted && !isTareaValid}>
                <InputLabel
                  sx={{
                    paddingTop: "0px",
                    "&.Mui-focused": {
                      paddingTop: "15px",
                    },
                  }}
                >
                  Tarea a realizar
                </InputLabel>
                <Select
                  name="tarea"
                  value={formData.tarea}
                  onChange={handleChange}
                >
                  {tareas.map((tarea) => (
                    <MenuItem key={tarea.id} value={tarea.id}>
                      {tarea.value}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                name="fechaLimite"
                label="Fecha límite"
                type="date"
                variant="outlined"
                value={formData.fechaLimite}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                fullWidth
                error={submitted && !isFechaLimiteValid}
                helperText={
                  submitted && !isFechaLimiteValid
                    ? "La fecha límite debe ser mayor a la de hoy."
                    : ""
                }
              />
              <p style={{ margin: 0, padding: 0, fontSize: 13 }}>
                * La fecha debe ser mayor a la fecha actual.
              </p>
              <FormControl fullWidth error={submitted && !isDepartamentoValid}>
                <InputLabel
                  sx={{
                    paddingTop: "0px",
                    "&.Mui-focused": {
                      paddingTop: "15px",
                    },
                  }}
                >
                  Departamento / Secretaría
                </InputLabel>
                <Select
                  name="departamento"
                  value={formData.departamento}
                  onChange={handleChange}
                >
                  {departamentos.map((departamento) => (
                    <MenuItem key={departamento.id} value={departamento.id}>
                      {departamento.value}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth error={submitted && !isMotivoValid}>
                <InputLabel
                  sx={{
                    paddingTop: "0px",
                    "&.Mui-focused": {
                      paddingTop: "15px",
                    },
                  }}
                >
                  Motivo de la traducción
                </InputLabel>
                <Select
                  name="motivo"
                  value={formData.motivo}
                  onChange={handleChange}
                >
                  {motivos.map((motivo) => (
                    <MenuItem key={motivo.id} value={motivo.id}>
                      {motivo.value}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl
                fullWidth
                error={submitted && !isProyectoInvestigacionValid}
              >
                <InputLabel
                  sx={{
                    paddingTop: "0px",
                    "&.Mui-focused": {
                      paddingTop: "15px",
                    },
                  }}
                >
                  ¿La traducción se enmarca en un proyecto de investigación
                  (PID-UTN)?
                </InputLabel>
                <Select
                  name="proyectoInvestigacion"
                  value={formData.proyectoInvestigacion}
                  onChange={handleChange}
                >
                  <MenuItem value="si">Sí</MenuItem>
                  <MenuItem value="no">No</MenuItem>
                </Select>
              </FormControl>
              <input
                type="file"
                name="adjunto"
                onChange={handleChange}
                accept=".docx"
                style={{ marginTop: "16px", marginBottom: 0 }}
              />
              <div>
                <p style={{ margin: 0, padding: 0, fontSize: 13 }}>
                  Tamaño máximo: 10 MB
                </p>
                <p style={{ margin: 0, padding: 0, fontSize: 13 }}>
                  Tipo de archivo permitido: .docx
                </p>
              </div>
              {submitted && !isAdjuntoValid && (
                <p style={{ color: "red" }}>Se requiere adjuntar un archivo</p>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <BotonSecondary onClick={onClose}>Cancelar</BotonSecondary>
            <SaveButton label={"Crear"} onClick={handleSubmit} type="button" />
          </DialogActions>
        </Box>
      )}
    </>
  );
};

export default IniciarSolicitudModal;
